import currency from 'currency.js';

import {
  shipmentLabel,
  shipmentInsurance,
  shipmentPackaging,
} from './reducers/shipments';

export function buildLegAddress(address) {
  const {
    address_1: address1,
    address_2: address2,
    attention_name: attentionName,
    country_code: countryCode,
    company_name: companyName,
    phone_number: phoneNumber,
    phone_country_code: phoneCountryCode,
    postal_code: postalCode,
    id,
    city,
    province,
    country,
    facility_id: facilityId,
    club_products: clubProducts,
    time_zone: timeZone,
  } = address;

  let addressType = 'residential';

  if (facilityId) {
    addressType = 'facility';
  }

  return {
    id,
    address1,
    address2,
    attentionName,
    companyName,
    city,
    province,
    postalCode,
    country,
    countryCode,
    addressType,
    phoneNumber,
    phoneCountryCode,
    facilityId,
    clubProducts,
    timeZone,
  };
}

export function buildLegFee(leg) {
  if (!leg.pickup) {
    return {};
  }

  const { fee } = leg;

  if (!fee) {
    return {};
  }

  const {
    id,
    hourly,
    price_cents: priceCents,
    ready_time: readyTime,
    ready_hour: readyHour,
    close_time: closeTime,
    close_hour: closeHour,
  } = fee;

  return {
    id,
    priceCents,
    readyTime,
    readyHour,
    closeTime,
    closeHour,
    hourly,
  };
}

export function buildLeg(leg) {
  const {
    id,
    name,
    state,
    index,
    pickup,
    editable: isEditable,
    uneditable_reasons: uneditableReasons,
    ship_date: shipDate,
    arrival_date: arrivalDate,
  } = leg;

  return {
    id,
    name,
    state,
    direction: name.toLowerCase(),
    method: pickup ? 'pickup' : 'dropOff',
    fee: buildLegFee(leg),
    origin: buildLegAddress(leg.origin),
    destination: buildLegAddress(leg.destination),
    index,
    isEditable,
    uneditableReasons,
    isCancelled: state === 'cancelled',
    shipDate,
    arrivalDate,
  };
}

export function buildTraveler(traveler) {
  const {
    id,
    email,
    name,
    phone,
    state,
    phone_country_code: phoneCountryCode,
    source_id: sourceId,
  } = traveler;

  const createdAt = parseInt(id.substring(0, 8), 16) * 1000;

  return {
    id,
    createdAt,
    email,
    name,
    phone,
    phoneCountryCode,
    state,
    sourceId,
  };
}

export function buildProductLine(productLine) {
  const {
    id,
    icon,
    domestic,
    disabled_countries: disabledCountries,
    international,
    name,
    public: isPublic,
    admin_only: adminOnly,
    color,
    display_name: displayName,
    display_name_plural: displayNamePlural,
  } = productLine;

  return {
    id,
    icon,
    domestic,
    disabledCountries,
    international,
    isPublic,
    adminOnly,
    color,
    displayName,
    displayNamePlural,
    name,
  };
}

export function buildLegShipment(shipment) {
  // console.log('buildLegShipment', shipment);

  const {
    id,
    state,
    parent_id: parentId,
    tracking_id: trackingId,
    shipment_id: shipmentId,
    ship_date: shipDate,
    arrival_date: arrivalDate,
    additional_info: additionalInfo,
    leg_id: legId,
    traveler_id: travelerId,
    total_price_cents: totalPriceCents,
  } = shipment;

  const label = shipmentLabel(shipment);
  const insurance = shipmentInsurance(shipment);
  const packaging = shipmentPackaging(shipment);

  const { coverage_amount_cents: coverageAmountCents } = insurance;

  return {
    id,
    state,
    parentId,
    trackingId,
    shipmentId,
    shipDate,
    arrivalDate,
    additionalInfo,
    legId,
    travelerId,
    totalPriceCents,
    alternatePrice: shipment.alternate_price,
    label: {
      id: label.id,
      displayName: label.display_name,
      priceCents: shipment.label_price_cents,
      carrierServiceLevelId: label.carrier_service_level_id,
      serviceLevel: label.service_level,
      carrier: label.carrier,
      productLine: buildProductLine(label.product_line),
      alternatePriceEnabled: label.alternate_price_enabled,
    },
    insurance: {
      id: insurance.id,
      coverageAmountCents,
      priceCents: shipment.insurance_price_cents,
      coverageAmount: currency(coverageAmountCents, {
        fromCents: true,
      }).format(),
    },
    packaging,
  };
}

export function buildOrder(payload) {
  return {
    id: payload.id,
    orderId: payload.order_id,
    isEditable: payload.editable,
    uneditableReason: payload.uneditable_reason,
  };
}

export function buildService(service) {
  const rates = {};
  const products = {};

  const {
    system_name: systemName,
    carrier_system_name: carrierSystemName,
    arrival_date: arrivalDate,
    display_name: displayName,
    transit_time: transitDays,
    carrier_service_level_id: carrierServiceLevelId,
    combined_price_cents: priceCents,
    best_value: isBestValue,
    carrier_service_level_hybrid: isHybrid,
    service_options: serviceOptions,
    real_carrier_system_name: realCarrierSystemName,
  } = service;

  serviceOptions.forEach((option) => {
    const { product_id: productId } = option;

    rates[productId] = option.service_rate_id;

    products[productId] = option.package_count;
  });

  return {
    systemName,
    carrierSystemName,
    arrivalDate,
    displayName,
    transitDays,
    carrierServiceLevelId,
    priceCents,
    isBestValue,
    isHybrid,
    rates,
    products,
    serviceOptions,
    realCarrierSystemName,
  };
}

export function buildFacility(facility) {
  const {
    club_id: id,
    name,
    icon,
  } = facility;

  const {
    address_1: address1,
    address_2: address2,
    city,
    state,
    zip: postalCode,
    phone_number: phoneNumber,
  } = facility.address;

  return {
    id,
    name,
    icon,
    address: {
      address1,
      address2,
      city,
      state,
      postalCode,
      phoneNumber,
    },
  };
}
